import { queryFirst, queryAll, loadScript, isInView, isInViewport } from 'lilly/domUtil';
import { EVENT_KEY_ENTER, EVENT_KEY_SPACE } from './constants';
import { scrollSheetTop, activateTab } from './components/modalApi';
import { appendToUrl } from './util';

const styliticsGallerySettings = queryFirst('#stylitics-stacked-gallery-settings')?.getAttribute('data-stylitics-gallery-settings');
const lookSelector = '.js-stylitics-widget-look';
const dialogSelector = '#stylitics-custom-gallery-modal';
const bundleSelector = '.js-stylitics-bundle-container';
const COMPOSER_CONTAINER_ID = 'stylitics-composer-container';

let modalTrackingBound = false;

const SELECTORS = {
    widget: {
        slides: '.stylitics-ui-carousel-card-list',
        outfit: '.stylitics-grid-outfit',
        cover:  '.stylitics-grid-outfit-cover',
        item:   '.stylitics-grid-item',
        link:   '.stylitics-grid-item-info'
    }
};
const RESPONSIVE_SETTINGS = {
    classic: [
        [0, { columns: 2 }],
        [767, { columns: 3 }],
        [1023, { columns: 4 }]
    ],
    widget: [
        [0, { columns: 1 }],
        [767, { columns: 2 }],
        [1023, { columns: 3 }]
    ]
};

let styliticsQvUrl;
const batches = [];
let scriptLoaded = false;
let resetTimeoutId = 0;
// Serializes initializeStyliticsGalleries(): a fast 2nd PLP update (e.g. color filter
// while sub-category init is still awaiting composer.start()) must not race the first.
let initInFlight = Promise.resolve();

/**
 * get widget's settings
 * @return {Object|boolean} widget settings
 */
function getSettings() {
    if (!styliticsGallerySettings || !queryFirst('gallery')) return false;

    let settings;
    try {
        settings = JSON.parse(styliticsGallerySettings);
    } catch (e) {
        return false;
    }
    styliticsQvUrl = settings.quickViewUrl;
    return settings;
}

/**
 * Updates the Stylitics sheet in the quick view modal.
 * @param {*} data - the outfit data
 * @param {string} styliticsPlacement - the placement for analytics (default: 'main')
 * @param {HTMLElement} [triggerEl] - the third-party carousel node that opened the sheet
 */
function updateStyliticsSheet(data, styliticsPlacement = 'main', triggerEl) {
    const parseBody = html => {
        const $html = $('<div>').append($.parseHTML(html));
        const body = $html.find(bundleSelector)[0];
        return body;
    };
    // get the modal and its look container
    const quickViewModal = queryFirst(dialogSelector);
    const $quickViewModal = $(`${dialogSelector} ${lookSelector}`);
    const $spinner = $quickViewModal.spinner();
    const tabToOpen = data.selectedItemIndex || 0;

    const styliticsQvUrlWithParams = appendToUrl(styliticsQvUrl, { outfitid: data.outfitID });
    const modalBody = queryFirst(lookSelector, quickViewModal);

    modalBody.innerHTML = '';
    $(quickViewModal).modal('show');
    $spinner.start();
    // fetch the outfit details and update the modal content
    fetch(styliticsQvUrlWithParams, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ styliticsOutfit: data })
    })
        .then(async (response) => {
            const text = await response.text();
            if (!response.ok) {
                $(quickViewModal).modal('hide');
                throw new Error('Network response was not ok');
            }
            return text;
        })
        .then(html => {
            const parsedBody = parseBody(html);
            if (modalBody && parsedBody) {
                modalBody.replaceChild(parsedBody, modalBody.firstChild);
            }
            const $sheet = $(modalBody).closest('.sheet');
            // add the data attribute container ID to the sheet for analytics purposes
            $sheet.attr('data-gallery-container-id', data.galleryContainerId || '');
            const outfitEl = triggerEl && triggerEl.closest && triggerEl.closest('.stylitics-grid-outfit');
            if (outfitEl) {
                $sheet.data('styliticsOutfitEl', outfitEl);
            }
            const $tabs = $sheet.find('.js-tab-controls button');
            const index = Number(tabToOpen);

            if ($tabs.length && Number.isInteger(index) && index >= 0 && index < $tabs.length) {
                activateTab($tabs.eq(index), styliticsPlacement);
            } else if ($tabs.length) {
                // fallback to first tab if index is invalid/out of range
                activateTab($tabs.eq(0), styliticsPlacement);
            }
            document.body.dispatchEvent(new CustomEvent('stylitics:bundle:tile:show', { detail: [modalBody] }));
            requestAnimationFrame(() => scrollSheetTop($(modalBody).closest('.sheet')));
            document.body.dispatchEvent(new CustomEvent('modalContentLoaded'));
        })
        .catch(() => {
            $(quickViewModal).modal('hide');
            // Optionally log error
        })
        .finally(() => {
            $spinner.stop();
        });
}
/**
 * Get outfit items
 * @param {*} items Object containing outfit items
 * @returns {Array} products array
 */
function getOutfitItems(items) {
    let products = Array.isArray(items)
        ? items.map(item => ({
            id: item.remote_id,
            image_url: item.small_image_url,
            stylitics_image_url: item.image_url,
            stylitics_small_image_url: item.small_image_url,
            original_image_url: item.client_original_image_url ? item.client_original_image_url.replace('$JPGOrig$', '$tile_xlarge-lg-1x$') : '',
            name: item.name,
            index: item.index,
            item_id: item.item_id
        }))
        : [];
    return products;
}

/**
 * Click handler for outfit
 * @param {*} data Object containing outfit and element
 * @returns {void}
 */
function clickOutfitHandler(data) {
    const { outfit, element } = data;
    if (!outfit || !element) return;
    // get ID of closest element container
    const galleryContainerId = element.closest('gallery')?.id || '';
    const outfitID = outfit.id;
    const result = {
        outfitID: outfitID,
        outfitIndex: outfit.index,
        galleryContainerId: galleryContainerId,
        products: []
    };
    if (outfit.items && outfit.items.length > 0) {
        // loop through outfit items to get product ids and images
        result.products = getOutfitItems(outfit.items);
    }
    // show QuickView modal
    updateStyliticsSheet(result, 'main', element);
}
/**
 * Click handler for item
 * @param {*} data Object containing item and element
 * @returns {void}
 */
function clickItemHandler(data) {
    const { item, outfit, element } = data;
    if (!item || !element) return;
    const galleryContainerId = element.closest('gallery')?.id || '';
    const result = {
        outfitID: outfit.id,
        outfitIndex: outfit.index,
        products: [],
        selectedItemIndex: item.index,
        galleryContainerId: galleryContainerId
    };
    if (outfit.items && outfit.items.length > 0) {
        // loop through outfit items to get product ids and images
        result.products = getOutfitItems(outfit.items);
    }
    // show QuickView modal
    updateStyliticsSheet(result, 'main', element);
}
/**
 * Build per-module config for a single gallery.
 * Type-dependent settings (responsive breakpoints, mobile peek/gutter) live here
 * so that mixed-type batches (e.g. classic + widget on the same page) each get
 * their own column counts. Composer merges these params on top of sharedParams.
 * @param {Object} galleryData - gallery settings
 * @return {Object} Stylitics module config
 */
function buildModuleConfig(galleryData) {
    const galleryType = galleryData.galleryType === 'widget' ? 'widget' : 'classic';
    const isWidget = galleryType === 'widget';
    return {
        target: galleryData.containerID,
        type: 'grid',
        params: {
            api: {
                tags: galleryData.galleryId
            },
            display: {
                nextOutfitPeekMobile: isWidget ? 0.1 : 0,
                gutterWidthMobile: isWidget ? 8 : 2
            },
            responsive: RESPONSIVE_SETTINGS[galleryType]
        },
        override: {
            'click.outfit': clickOutfitHandler,
            'click.item': clickItemHandler
        }
    };
}

/**
 * Build shared params applied across all modules in a Composer instance.
 * Only type-agnostic settings go here; per-type settings (responsive,
 * nextOutfitPeekMobile, gutterWidthMobile) belong in each module's params so
 * mixed-type pages render each gallery with the correct column count.
 * OnetrustActiveGroups is a global data layer object set by OneTrust.
 * @return {Object} Stylitics sharedParams
 */
function buildSharedParams() {
    const sharedParams = {
        api: {
            min: 3,
            max: 6
        },
        analytics: {
            placement: 'gallery'
        },
        display: {
            isGallery: true,
            verticalLayout: true,
            verticalAnchorLayout: true,
            editorialShopTheModel: true,
            hideAnchorItem: true,
            showMobileArrows: true,
            showPlusIcon: true,
            itemInfoStatic: true,
            showCarouselDots: true
        }
    };
    const onetrustActiveGroupsValue = window.OnetrustActiveGroups || '';
    if (!onetrustActiveGroupsValue || !~onetrustActiveGroupsValue.indexOf('C0003')) {
        sharedParams.customer = { enableTracking: false };
    }
    return sharedParams;
}

/**
 * Find the Stylitics module instance for a given gallery container ID across all batches.
 * @param {string} galleryContainerId - the gallery container DOM id
 * @return {Object|null} Stylitics module instance
 */
function findModuleForContainer(galleryContainerId) {
    for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const idx = batch.moduleIndexMap[galleryContainerId];
        if (idx !== undefined) return batch.composer.modules && batch.composer.modules[idx];
    }
    return null;
}

/**
 * Best-effort teardown of a Stylitics Composer instance (same container IDs can be
 * re-used on new DOM nodes after PLP grid refresh (filter, sort, show-more); the old instance must
 * not keep running against detached nodes). StyliticsComposer.destroy() is async
 * (see web-assets.stylitics.com loader composer.js).
 * @param {Object|undefined} composer - Stylitics Composer instance
 * @returns {Promise<void>} resolves when SDK destroy finishes (or skips if no destroy)
 */
async function disposeComposer(composer) {
    if (!composer || typeof composer.destroy !== 'function') return;
    try {
        await Promise.resolve(composer.destroy());
    } catch {
        // SDK may throw if targets are already detached
    }
}

/**
 * Drop Composer batches that own any of the given gallery container ids, dispose
 * instances, and remove the global Composer host so the next init starts clean.
 * @param {Set<string>} containerIds - set of gallery element id strings
 * @returns {Promise<void>} resolves after matching composers are destroyed
 */
async function disposeBatchesForContainerIds(containerIds) {
    const toDispose = [];
    const nextBatches = [];
    for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const keys = Object.keys(batch.moduleIndexMap);
        if (keys.some(k => containerIds.has(k))) {
            toDispose.push(batch.composer);
        } else {
            nextBatches.push(batch);
        }
    }
    if (!toDispose.length) return;
    await Promise.allSettled(toDispose.map(c => disposeComposer(c)));
    batches.length = 0;
    batches.push(...nextBatches);
    const globalHost = queryFirst('#' + COMPOSER_CONTAINER_ID);
    if (globalHost?.parentNode) {
        globalHost.parentNode.removeChild(globalHost);
    }
}

/**
 * Drop Composer batches whose gallery containers are no longer attached to the document
 * (e.g. PLP filter/sub-cat/pagination response that doesn't include the slot). Without
 * this, the SDK keeps internal observers/timers running against detached nodes, which
 * later throws "Invalid array length" when the slot reappears with the same id.
 * @returns {Promise<void>} resolves after orphaned composers are destroyed
 */
async function disposeOrphanedBatches() {
    if (!batches.length) return;
    const orphanedIds = new Set();
    for (let i = 0, len = batches.length; i < len; i++) {
        const keys = Object.keys(batches[i].moduleIndexMap);
        const allDetached = keys.every(id => !document.getElementById(id));
        if (allDetached) keys.forEach(id => orphanedIds.add(id));
    }
    if (orphanedIds.size) await disposeBatchesForContainerIds(orphanedIds);
}

/** Resets the accessibility attributes for outfit covers based on their visibility.
 * @param {Array} covers - array of outfit cover elements
 * @param {function(HTMLElement): boolean} [visibilityPredicate] - default isInView (~99% visible); PLP slots use looser check
 */
function resetOutfitCoversAccessibility(covers, visibilityPredicate = isInView) {
    if (!covers || !covers.length) return;

    covers.forEach(cover => {
        if (!visibilityPredicate(cover)) {
            cover.removeAttribute('tabindex');
            cover.removeAttribute('role');
            cover.removeAttribute('aria-label');
            return;
        }

        cover.setAttribute('tabindex', '0');
        cover.setAttribute('role', 'button');
        cover.setAttribute('aria-label', 'View outfit items');
    });
}

/** Updates the dots line position based on the active dot.
 * @param {HTMLElement} dotsContainer - the container element for the dots
 * @param {Array} covers - array of outfit cover elements
 */
function updateDotsLine(dotsContainer, covers) {
    if (!dotsContainer) return;

    const activeDot = queryFirst('.stylitics-ui-current-dot', dotsContainer);
    if (!activeDot) return;

    const dotsNumber = dotsContainer.childElementCount;

    dotsContainer.setAttribute('style', `--line-width:calc(100% / ${dotsNumber});--line-offset-x:${activeDot.offsetLeft}px;`);
}

/** A11y for covers
 * Schedules a reset of outfit covers' accessibility attributes.
 * @param {Array} currentCovers - array of outfit cover elements
 * @param {function(HTMLElement): boolean} [visibilityPredicate]
 */
function scheduleResetOutfitCoversAccessibility(currentCovers, visibilityPredicate) {
    if (resetTimeoutId) window.clearTimeout(resetTimeoutId);

    resetTimeoutId = window.setTimeout(() => {
        resetOutfitCoversAccessibility(currentCovers, visibilityPredicate);
    }, 220);
}

/** Observe slides and setup necessary handlers
 * @param {string} galleryType - type of the gallery ('classic' or 'widget')
 * @param {HTMLElement} styliticsWidget - the Stylitics widget element
 * @return {void}
 */
function observeSlides(galleryType, styliticsWidget) {
    if (!styliticsWidget) return;

    // PLP inline slot: strict isInView + 0.95 IO threshold often leave covers without tabindex on first paint
    // (layout, grid reorder, partial width). Looser viewport check + lower IO threshold matches post-click / refocus behavior.
    const relaxSlotVisibility = Boolean(styliticsWidget.closest('.category-marketing-slot'));
    const coverVisibilityPredicate = relaxSlotVisibility ? isInViewport : isInView;
    const coverIntersectMin = relaxSlotVisibility ? 0.01 : 0.95;

    const { slides: slidesClass, cover: coverClass } = SELECTORS[galleryType] || SELECTORS.widget;
    const covers = queryAll(coverClass, styliticsWidget);

    if (!covers.length) {
        window.setTimeout(() => observeSlides(galleryType, styliticsWidget), 50);
        return;
    }

    // =========================
    // Dots line (lazy bind)
    // =========================
    let dotsContainer = null;
    let dotsObserver = null;

    const onDotsMutation = mutations => {
        const size = mutations.length;
        for (let i = 0, n = size; i < n; i++) {
            const m = mutations[i];
            if (m.type !== 'attributes' || m.attributeName !== 'class') continue;

            const el = m.target;
            if (!(el instanceof HTMLElement)) continue;

            if (
                el.classList.contains('stylitics-ui-current-dot')
                || (m.oldValue && m.oldValue.indexOf('stylitics-ui-current-dot') !== -1)
            ) {
                const dotsListEl = dotsContainer;
                requestAnimationFrame(() => updateDotsLine(dotsListEl, covers));
                scheduleResetOutfitCoversAccessibility(covers, coverVisibilityPredicate);
                break;
            }
        }
    };

    const ensureDots = () => {
        const nextDotsContainer = queryFirst('.stylitics-ui-dots-list', styliticsWidget);
        if (!nextDotsContainer) return;

        if (dotsContainer === nextDotsContainer) {
            requestAnimationFrame(() => updateDotsLine(dotsContainer, covers));
            return;
        }

        dotsContainer = nextDotsContainer;

        updateDotsLine(dotsContainer, covers);

        if (dotsObserver) dotsObserver.disconnect();

        dotsObserver = new MutationObserver(onDotsMutation);
        dotsObserver.observe(dotsContainer, {
            subtree: true,
            attributes: true,
            attributeFilter: ['class'],
            attributeOldValue: true
        });
    };

    // initial try
    ensureDots();

    // If dots appear later (landscape -> portrait), bind on resize/orientation
    let resizeRaf = 0;
    const onResize = () => {
        if (resizeRaf) return;
        resizeRaf = requestAnimationFrame(() => {
            resizeRaf = 0;
            ensureDots();
        });
    };

    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });

    // Initial sync
    resetOutfitCoversAccessibility(covers, coverVisibilityPredicate);

    // IntersectionObserver
    const slidesContainer = queryFirst(slidesClass, styliticsWidget) || styliticsWidget;

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            const cover = entry.target;

            if (entry.intersectionRatio >= coverIntersectMin) {
                cover.setAttribute('tabindex', '0');
                cover.setAttribute('role', 'button');
                cover.setAttribute('aria-label', 'View outfit items');
            } else {
                cover.removeAttribute('tabindex');
                cover.removeAttribute('role');
                cover.removeAttribute('aria-label');
            }
        });

        scheduleResetOutfitCoversAccessibility(covers, coverVisibilityPredicate);
        ensureDots(); // optional safety net, cheap
    }, { threshold: coverIntersectMin });

    covers.forEach(cover => observer.observe(cover));

    if (relaxSlotVisibility) {
        window.setTimeout(() => {
            scheduleResetOutfitCoversAccessibility(covers, coverVisibilityPredicate);
        }, 320);
    }

    // =========================
    // Widget: react to slide changes
    // =========================
    if (slidesContainer) {
        slidesContainer.addEventListener(
            'scroll',
            () => {
                scheduleResetOutfitCoversAccessibility(covers, coverVisibilityPredicate);
                ensureDots(); // optional: in case dots appear after internal relayout
            },
            { passive: true }
        );

        slidesContainer.addEventListener('transitionend', () => {
            scheduleResetOutfitCoversAccessibility(covers, coverVisibilityPredicate);
            ensureDots();
        });

        const slidesMutationObserver = new MutationObserver(mutations => {
            const size = mutations.length;
            for (let i = 0, n = size; i < n; i++) {
                const m = mutations[i];
                if (m.type !== 'attributes') continue;

                scheduleResetOutfitCoversAccessibility(covers, coverVisibilityPredicate);
                ensureDots();
                break;
            }
        });

        slidesMutationObserver.observe(slidesContainer, {
            attributes: true,
            attributeFilter: ['style', 'class']
        });
    }
}

/** Click first item link from cover element
 * @param {HTMLElement} coverEl - cover element
 * @param {Object} galleryClasses -  gallery classes based on gallery type
 *  @return {void}
 */
function clickFirstItemFromCover(coverEl, galleryClasses) {
    const outfitEl = coverEl.closest(galleryClasses.outfit);
    if (!outfitEl) return;

    const firstItem = queryFirst(galleryClasses.item, outfitEl);
    if (!firstItem) return;

    const link = queryFirst(galleryClasses.link, firstItem);
    if (!link) return;

    link.click();
}
/** Setup mobile tap vs swipe fix
 *`cover` tap should open the outfit, but swipe/scroll should not
 * @param {Object} galleryClasses -  gallery classes based on gallery type
 * @param {HTMLElement} styliticsWidget - the Stylitics widget element
 */
function setupMobileTapVsSwipeFix(galleryClasses, styliticsWidget) {
    const TAP_MOVE_THRESHOLD_PX = 10;
    const TAP_TIME_THRESHOLD_MS = 350;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let trackingCover = null;
    let moved = false;
    styliticsWidget.addEventListener(
        'touchstart',
        event => {
            const coverEl = event.target.closest(galleryClasses.cover);
            if (!coverEl) return;

            if (!event.touches || event.touches.length !== 1) return;

            trackingCover = coverEl;
            moved = false;

            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            startTime = Date.now();
        },
        { capture: true, passive: true }
    );

    styliticsWidget.addEventListener(
        'touchmove',
        event => {
            if (!trackingCover) return;
            if (!event.touches || event.touches.length !== 1) return;

            const dx = Math.abs(event.touches[0].clientX - startX);
            const dy = Math.abs(event.touches[0].clientY - startY);

            if (dx > TAP_MOVE_THRESHOLD_PX || dy > TAP_MOVE_THRESHOLD_PX) {
                moved = true;
                trackingCover = null; // treat as swipe/scroll
            }
        },
        { capture: true, passive: true }
    );

    styliticsWidget.addEventListener(
        'touchend',
        event => {
            if (!trackingCover) return;

            const elapsed = Date.now() - startTime;

            // If it was a swipe or a long press — do nothing
            if (moved || elapsed > TAP_TIME_THRESHOLD_MS) {
                trackingCover = null;
                return;
            }

            // It is a tap → run our handler
            event.preventDefault();
            event.stopPropagation();
            if (event.stopImmediatePropagation) event.stopImmediatePropagation();

            clickFirstItemFromCover(trackingCover, galleryClasses);
            trackingCover = null;
        },
        { capture: true, passive: false }
    );
}

/**
 * Check if the event is an Enter or Space key event
 * @param {KeyboardEvent} event - the keyboard event
 * @return {boolean} true if the event is Enter or Space key, false otherwise
 */
function isEnterOrSpace(event) {
    return event.key === EVENT_KEY_ENTER || event.key === EVENT_KEY_SPACE;
}

/**
 * Bind DOM event listeners for a single gallery widget.
 * No-op if the widget was already bound in a previous initialization.
 * @param {Object} galleryClasses - selector strings for this gallery type
 * @param {HTMLElement} styliticsWidget - the gallery root DOM element
 */
function setupGalleryListeners(galleryClasses, styliticsWidget) {
    if (styliticsWidget.dataset.styliticsBound === 'true') return;
    styliticsWidget.dataset.styliticsBound = 'true';

    const { cover: coverClass, item: itemClass, link: actualLinkClass } = galleryClasses;

    setupMobileTapVsSwipeFix(galleryClasses, styliticsWidget);

    styliticsWidget.addEventListener('click', event => {
        const coverEl = event.target.closest(coverClass);
        if (!coverEl) return;
        event.preventDefault();
        clickFirstItemFromCover(coverEl, galleryClasses);
    });

    styliticsWidget.addEventListener('keydown', event => {
        const coverEl = event.target.closest(coverClass);
        if (!coverEl) return;
        if (!isEnterOrSpace(event)) return;
        event.preventDefault();
        clickFirstItemFromCover(coverEl, galleryClasses);
    });

    styliticsWidget.addEventListener(
        'keydown',
        event => {
            const itemEl = event.target.closest(itemClass);
            if (!itemEl) return;
            if (!isEnterOrSpace(event)) return;
            if (event.target.closest(actualLinkClass)) return;
            const link = queryFirst(actualLinkClass, itemEl);
            if (!link) return;
            event.preventDefault();
            link.click();
        },
        true
    );
}

/**
 * @param {Object} settings - global widget settings from the page
 * @param {Document|HTMLElement} target - search root (defaults to document)
 */
async function initializeStyliticsGalleriesInternal(settings, target) {
    const root = target || document;

    // Always clean up batches whose <gallery> nodes were removed from DOM by the
    // previous PLP update (filter/sub-cat/pagination that returned no slot).
    await disposeOrphanedBatches();

    const galleryContainers = queryAll('gallery', root);
    if (!galleryContainers || !galleryContainers.length) return;

    const uninitialized = galleryContainers.filter(el => el.dataset.styliticsInitialized !== 'true');
    if (!uninitialized.length) return;

    const galleriesData = uninitialized.map(element => {
        element.removeAttribute('data-stylitics-initialized');
        element.dataset.styliticsInitialized = 'true';
        return {
            galleryType: element.dataset.galleryType,
            galleryId: element.dataset.galleryTag,
            containerID: element.id
        };
    });

    await disposeBatchesForContainerIds(new Set(galleriesData.map(gd => gd.containerID)));

    galleriesData.forEach(gd => {
        const widget = queryFirst('#' + gd.containerID, root);
        if (!widget) return;
        widget.innerHTML = '';
        $(widget).spinner().start();
    });
    // create composer container if it doesn't exist
    let composerContainer = queryFirst('#' + COMPOSER_CONTAINER_ID);
    if (!composerContainer) {
        composerContainer = document.createElement('div');
        composerContainer.id = COMPOSER_CONTAINER_ID;
        document.body.appendChild(composerContainer);
    }

    const moduleIndexMap = {};
    galleriesData.forEach((gd, i) => {
        moduleIndexMap[gd.containerID] = i;
    });

    let composer;
    try {
        // Defer until the next paint frame so layout can settle after DOM updates.
        await new Promise(resolve => {
            requestAnimationFrame(resolve);
        });
        // eslint-disable-next-line no-undef
        composer = await StyliticsComposer.initialize({
            client: settings.clientID,
            target: COMPOSER_CONTAINER_ID,
            config: {
                modules: galleriesData.map(buildModuleConfig),
                sharedParams: buildSharedParams()
            }
        });
        await composer.start();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[Stylitics] Composer init error:', e);
        galleriesData.forEach(gd => {
            const widget = queryFirst('#' + gd.containerID, root);
            if (widget) {
                $(widget).spinner().stop();
                widget.removeAttribute('data-stylitics-initialized');
            }
        });
        return;
    }

    batches.push({ composer, moduleIndexMap });
    resetTimeoutId = 0;

    galleriesData.forEach(gd => {
        const galleryType = gd.galleryType || 'classic';
        const galleryClasses = SELECTORS[galleryType] || SELECTORS.widget;
        const styliticsWidget = queryFirst('#' + gd.containerID, root);
        if (!styliticsWidget) return;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => observeSlides(galleryType, styliticsWidget));
            $(styliticsWidget).spinner().stop();
        });

        setupGalleryListeners(galleryClasses, styliticsWidget);
    });

    bindModalTrackingOnce();
}

/**
 * Public entry point. Serializes initializeStyliticsGalleriesInternal via initInFlight so
 * a fast 2nd PLP update can't race the first (e.g. color filter while sub-cat is still
 * awaiting composer.start()).
 * @param {Object} settings - global widget settings from the page
 * @param {Document|HTMLElement} [target] - search root (defaults to document)
 * @returns {Promise<void>} resolves when this init finishes
 */
function initializeStyliticsGalleries(settings, target) {
    const next = initInFlight.then(() => initializeStyliticsGalleriesInternal(settings, target));
    initInFlight = next.catch(() => {});
    return next;
}

/**
 * Loads Stylitics script (if needed) and initializes galleries on the page.
 * Always tears down orphaned composers (whose <gallery> was removed by the latest
 * PLP update) so they don't crash the SDK when the slot reappears with the same id.
 * @return {void}
 */
function loadStyliticsContent() {
    const settings = getSettings();
    if (!settings || !settings.script) {
        // No <gallery> in DOM right now — still drop stale composers so the next
        // PLP update that does include the slot starts from a clean slate.
        if (scriptLoaded) {
            initInFlight = initInFlight.then(() => disposeOrphanedBatches()).catch(() => {});
        }
        return;
    }

    if (scriptLoaded) {
        initializeStyliticsGalleries(settings);
        return;
    }

    loadScript(settings.script, true).then(function () {
        scriptLoaded = true;
        initializeStyliticsGalleries(settings);
    });
}
/**
 * Binds modal-related analytics tracking listeners once.
 * Uses findModuleForContainer to resolve the correct Stylitics module
 * regardless of which Composer batch the gallery belongs to.
 * @return {void}
 */
function bindModalTrackingOnce() {
    if (modalTrackingBound) return;

    const quickViewModal = queryFirst(dialogSelector);
    if (!quickViewModal) return;

    modalTrackingBound = true;

    quickViewModal.addEventListener('stylitics:completeLook:addItemToCart', event => {
        const data = event?.detail;
        const { galleryContainerId } = (event?.currentTarget || {}).dataset || {};
        const module = findModuleForContainer(galleryContainerId);
        if (!module || !data?.pid) return;

        const analyticsData = { itemId: data.pid, placement: 'widget' };
        if (data.price) analyticsData.price = data.price.split('$').pop();
        module.trackAddItemToCart(analyticsData);
    });

    quickViewModal.addEventListener('stylitics:completeLook:trackItemClick', event => {
        const data = event?.detail;
        const { galleryContainerId } = (event?.currentTarget || {}).dataset || {};
        const module = findModuleForContainer(galleryContainerId);
        if (!module || !data?.pid) return;

        module.trackClickItem(data.pid, { outfitId: data.outfitId ? Number(data.outfitId) : null });
    });

    quickViewModal.addEventListener('tabActivated', event => {
        const { detail, target } = event;
        if (!detail?.tabActive?.dataset || !target) return;

        const { galleryContainerId } = (event?.currentTarget || {}).dataset || {};
        const module = findModuleForContainer(galleryContainerId);
        if (!module) return;

        const { styliticsProductId } = detail.tabActive.dataset;
        if (!styliticsProductId) return;

        const completeLookContainer = target.closest('.js-complete-look-container');
        const { styliticsOutfitid } = completeLookContainer?.dataset || {};
        const analyticsData = { outfitId: styliticsOutfitid ? Number(styliticsOutfitid) : null };

        // The Stylitics script deduplicates view events, so tracking here is safe
        // even if the main widget already sent it
        module.trackViewItem(styliticsProductId, analyticsData);
        // Skip click tracking for the main bundle tab to avoid double-counting
        if (detail.styliticsPlacement === 'main') return;
        module.trackClickItem(styliticsProductId, analyticsData);
    });
}

window.addEventListener('load', function () {
    loadStyliticsContent();
});

$('body').on('search:updateProducts', loadStyliticsContent);
